import { addressToText } from '@dadohr/shared/domainTypes/address';
import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  CustomFieldKey,
  CustomUserField,
  IOrganization,
  toCustomFieldKey
} from '@dadohr/shared/domainTypes/org';
import { Timestamp } from '@dadohr/shared/domainTypes/time';
import { IUser, StandardUserFieldKey } from '@dadohr/shared/domainTypes/user';
import { capitalize, compact } from 'lodash';
import { useMemo } from 'react';
import { DataGrid, DataGridItem } from '../../../components/DataGrid';
import { LazyUserById } from '../../../components/UserTable/LazyUser';
import { UnsafeTFunction, useTranslation } from '../../../i18next';
import {
  formatPossiblyYearlessMoment,
  toMomentFromAny
} from '../../../services/time';

const formatDate = (
  ts: Timestamp | { _seconds: number; _nanoseconds: number } | null | undefined,
  dateFormats: IOrganization['dateFormats'],
  opts: { includeTime?: boolean; hideYear?: boolean } = {}
) => {
  if (ts === null || ts === undefined) {
    return '';
  }
  try {
    const m = toMomentFromAny(ts);
    if (!m.isValid()) {
      return '';
    }
    const dateFormat = opts.includeTime
      ? `${dateFormats.MMDDYYYY} HH:mm`
      : dateFormats.MMDDYYYY;

    return formatPossiblyYearlessMoment(m, dateFormat, opts.hideYear);
  } catch {
    return '';
  }
};

const LazyUserWithLinkToProfile = ({ userId }: { userId: string | null }) => {
  if (!userId) {
    return <>{'-'}</>;
  }
  return (
    <LazyUserById userId={userId} includeLinkToUserProfile emptyDefault={'-'} />
  );
};

const booleanToText = (x: boolean, t: UnsafeTFunction) => (x ? 'Yes' : t('No'));

export const STANDARD_FIELDS: {
  [K in StandardUserFieldKey]: (
    d: Doc<IUser>,
    t: UnsafeTFunction,
    dateFormats: IOrganization['dateFormats']
  ) => DataGridItem | null;
} = {
  alternateId: (d, t) => ({
    label: 'Alternate ID',
    d: d.data.alternateId
  }),
  active: () => null,
  department: (d, t) => ({
    label: t('Department'),
    d: d.data.department
  }),
  hireDate: (d, t, df) => ({
    label: t('First Day at Work'),
    d: formatDate(d.data.hireDate, df)
  }),
  gender: () => null,
  terminationDate: (d, t, df) => ({
    label: t('Last Day at Work'),
    d: formatDate(d.data.terminationDate, df)
  }),
  dateOfBirth: (d, t, df) => ({
    label: t('Date of Birth'),
    d: formatDate(d.data.dateOfBirth, df, { hideYear: true })
  }),
  firstName: (d, t) => ({
    label: t('First Name'),
    d: d.data.firstName
  }),
  preferredName: (d, t) => ({
    label: t('Preferred Name'),
    d: d.data.preferredName
  }),
  middleName: () => null,
  lastName: (d, t) => ({
    label: t('Last Name'),
    d: d.data.lastName
  }),
  location: (d, t) => ({
    label: t('Location'),
    d: d.data.location
  }),
  managerId: (d, t) => ({
    label: t('Manager'),
    d: <LazyUserWithLinkToProfile userId={d.data.managerId} />
  }),
  email: (d, t) => ({
    label: capitalize(t('email')),
    d: d.data.email
  }),
  personalEmail: (d, t) => ({
    label: t('Personal Email'),
    d: d.data.personalEmail
  }),
  role: (d, t) => ({
    label: t('Role'),
    d: d.data.role
  }),
  team: (d, t) => ({
    label: t('Team'),
    d: d.data.team
  }),
  phoneNumber: (d, t) => ({
    label: t('Phone Number'),
    d: d.data.phoneNumber
  }),
  address: (d, t) => ({
    label: t('Address'),
    d: addressToText(d.data.address)
  })
};

const renderCustomFieldValue = (
  field: CustomUserField,
  d: Doc<IUser>,
  t: UnsafeTFunction,
  dateFormats: IOrganization['dateFormats']
): DataGridItem['d'] => {
  const val = d.data.customFields[field.key] as any;
  switch (field.type) {
    case 'address':
      return addressToText(val);
    case 'boolean':
      return booleanToText(val, t);
    case 'date':
      return formatDate(val, dateFormats, {
        includeTime: true,
        hideYear: field.hideYear
      });
    case 'smartDate':
      return formatDate(val, dateFormats, { includeTime: true, hideYear: false });
    case 'relationship':
      return <LazyUserWithLinkToProfile userId={val} />;
    case 'string':
    case 'number':
    case 'smartText':
      // Boolean values can land in a string-typed custom field via
      // integration imports (e.g., Greenhouse Yes/No). Coerce to "Yes"/"No"
      // so `false` doesn't render as the bare boolean (or blank, depending
      // on the data-grid renderer's handling).
      if (typeof val === 'boolean') return booleanToText(val, t);
      return val;
    default:
      return null;
  }
};

const renderCustomField = (
  field: CustomUserField
): ((
  d: Doc<IUser>,
  t: UnsafeTFunction,
  dateFormats: IOrganization['dateFormats']
) => DataGridItem | null) => {
  return (d, t, df) => ({
    label: field.label,
    d: renderCustomFieldValue(field, d, t, df)
  });
};

export const useAllUserDataGridFields = (customFields: CustomUserField[]) => {
  return useMemo<
    {
      [K in StandardUserFieldKey | CustomFieldKey]: (
        d: Doc<IUser>,
        t: UnsafeTFunction,
        dateFormats: IOrganization['dateFormats']
      ) => DataGridItem | null;
    }
  >(() => {
    return {
      ...STANDARD_FIELDS,
      ...Object.fromEntries(
        customFields.map((f) => [toCustomFieldKey(f.key), renderCustomField(f)])
      )
    };
  }, [customFields]);
};

export const UserDataGrid = ({
  d,
  fields,
  org
}: {
  d: Doc<IUser>;
  fields: (StandardUserFieldKey | CustomFieldKey)[];
  org: IOrganization;
}) => {
  const { dateFormats, customUserFields } = org;
  const allFields = useAllUserDataGridFields(customUserFields);
  const { t } = useTranslation();
  const ds = useMemo(
    () => compact(fields.map((f) => allFields[f](d, t, dateFormats))),
    [d, dateFormats, t, fields, allFields]
  );
  return <DataGrid ds={ds} emptyDefault={'-'} />;
};
